import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BookOpen, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Plus, X, Edit2, Check, Trash2, Search, GitMerge, AlertTriangle, Zap, Loader2 } from 'lucide-react'
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../lib/api'
import type { Profession, PendingProfessionRequest } from '../../../shared/types'

const US_STATES: Record<string, string> = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
  "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
  "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
  "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
  "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
  "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
  "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
  "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming"
}

const PROFESSION_CATEGORIES = [
  'Healthcare',
  'Allied Health',
  'Mental Health',
  'Emergency Services',
  'Dental',
  'Pharmacy',
  'Nursing',
  'Other'
]

interface PendingRequestsResponse {
  requests: PendingProfessionRequest[]
}

type ProfessionsResponse = Profession[]

interface CECategory {
  name: string
  requiredHours?: number
  isPediatricContent?: boolean
  isOneTime?: boolean
}

interface CeRequirementWithCategories {
  id: string
  professionId: string
  state: string
  totalHours: number | null
  renewalPeriodYears: number
  notes: string | null
  isActive: boolean
  followsNremt: boolean
  categories: Array<{
    id: string
    name: string
    requiredHours: string | null
    isPediatricContent: boolean
    isOneTime: boolean
  }>
}

export default function CEManagementPage() {
  const queryClient = useQueryClient()
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null)
  const [expandedProfession, setExpandedProfession] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'requests' | 'professions' | 'missing'>('requests')
  const [professionSearchQuery, setProfessionSearchQuery] = useState('')
  
  // Missing Requirements State
  const [lookupProfessionId, setLookupProfessionId] = useState<string | null>(null)
  const [lookupState, setLookupState] = useState('')
  const [lookupResults, setLookupResults] = useState<{
    professionId: string
    professionName: string
    state: string
    totalHours: number
    renewalPeriodYears: number
    categories: Array<{ name: string; hours: number | null; isRequired: boolean; isOneTime: boolean }>
    specialRequirements: string
    confidence: number
  } | null>(null)
  const [editingLookupResults, setEditingLookupResults] = useState(false)
  const [lookupEditHours, setLookupEditHours] = useState('')
  const [lookupEditRenewalYears, setLookupEditRenewalYears] = useState('')
  const [lookupEditNotes, setLookupEditNotes] = useState('')
  const [lookupEditCategories, setLookupEditCategories] = useState<Array<{ name: string; hours: string; isOneTime: boolean; isPediatric: boolean }>>([])
  const [lookupNewCatName, setLookupNewCatName] = useState('')
  const [lookupNewCatHours, setLookupNewCatHours] = useState('')

  // Add Profession Modal State
  const [showAddModal, setShowAddModal] = useState(false)
  const [newProfessionName, setNewProfessionName] = useState('')
  const [newAbbreviation, setNewAbbreviation] = useState('')
  const [newCategory, setNewCategory] = useState('Healthcare')
  const [newState, setNewState] = useState('')
  const [newTotalHours, setNewTotalHours] = useState('')
  const [newRenewalYears, setNewRenewalYears] = useState('2')
  const [newSpecialReqs, setNewSpecialReqs] = useState('')
  const [newCeCategories, setNewCeCategories] = useState<CECategory[]>([])
  const [newCatName, setNewCatName] = useState('')
  const [newCatHours, setNewCatHours] = useState('')
  
  // Edit CE Requirement State
  const [editingRequirementId, setEditingRequirementId] = useState<string | null>(null)
  const [editTotalHours, setEditTotalHours] = useState('')
  const [editRenewalYears, setEditRenewalYears] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editFollowsNremt, setEditFollowsNremt] = useState(false)
  
  // Category Management State
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null)
  const [editCatName, setEditCatName] = useState('')
  const [editCatHours, setEditCatHours] = useState('')
  const [editCatIsOneTime, setEditCatIsOneTime] = useState(false)
  const [editCatIsPediatric, setEditCatIsPediatric] = useState(false)
  const [addingCategoryToReqId, setAddingCategoryToReqId] = useState<string | null>(null)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryHours, setNewCategoryHours] = useState('')
  const [newCategoryIsOneTime, setNewCategoryIsOneTime] = useState(false)
  const [newCategoryIsPediatric, setNewCategoryIsPediatric] = useState(false)

  // Add CE Requirement State
  const [addingRequirementToProfId, setAddingRequirementToProfId] = useState<string | null>(null)
  const [newReqState, setNewReqState] = useState('')
  const [newReqTotalHours, setNewReqTotalHours] = useState('')
  const [newReqRenewalYears, setNewReqRenewalYears] = useState('2')
  const [newReqNotes, setNewReqNotes] = useState('')

  // Edit Profession State
  const [editingProfessionId, setEditingProfessionId] = useState<string | null>(null)
  const [editProfessionName, setEditProfessionName] = useState('')
  const [editProfessionAbbreviation, setEditProfessionAbbreviation] = useState('')
  const [editProfessionCategory, setEditProfessionCategory] = useState('')
  const [editProfessionIsActive, setEditProfessionIsActive] = useState(true)

  // Delete/Merge Profession State
  const [deletingProfessionId, setDeletingProfessionId] = useState<string | null>(null)
  const [mergingProfession, setMergingProfession] = useState<Profession | null>(null)
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null)
  const [mergeSearchQuery, setMergeSearchQuery] = useState('')

  const { data: requestsData, isLoading: loadingRequests } = useQuery({
    queryKey: ['admin', 'profession-requests'],
    queryFn: () => apiGet<PendingRequestsResponse>('/admin/profession-requests'),
  })

  const { data: professionsData, isLoading: loadingProfessions } = useQuery({
    queryKey: ['professions'],
    queryFn: () => apiGet<ProfessionsResponse>('/professions'),
  })

  // Fetch requirements for expanded profession
  const { data: professionRequirements, isLoading: loadingRequirements } = useQuery({
    queryKey: ['admin', 'profession-requirements', expandedProfession],
    queryFn: () => apiGet<CeRequirementWithCategories[]>(`/admin/professions/${expandedProfession}/requirements`),
    enabled: !!expandedProfession,
  })

  // Fetch professions without CE requirements (with requested states)
  interface ProfessionWithRequestedStates extends Profession {
    requestedStates: { state: string; userCount: number }[]
  }
  const { data: missingReqsProfessions, isLoading: loadingMissing } = useQuery({
    queryKey: ['admin', 'professions-missing-requirements'],
    queryFn: () => apiGet<ProfessionWithRequestedStates[]>('/admin/professions/missing-requirements'),
  })

  // Fetch professions with user-requested states missing CE requirements
  interface MissingStateResult {
    profession: Profession
    missingStates: { state: string; userCount: number }[]
  }
  const { data: missingStateReqs, isLoading: loadingMissingStates } = useQuery({
    queryKey: ['admin', 'professions-missing-state-requirements'],
    queryFn: () => apiGet<MissingStateResult[]>('/admin/professions/missing-state-requirements'),
  })

  // Handle both array response and object with professions key
  const professions = Array.isArray(professionsData) 
    ? professionsData 
    : (professionsData as unknown as { professions: Profession[] })?.professions || []

  const approveMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/profession-requests/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requests'] })
      queryClient.invalidateQueries({ queryKey: ['professions'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/profession-requests/${id}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requests'] })
    },
  })

  const createProfessionMutation = useMutation({
    mutationFn: (data: {
      name: string
      abbreviation?: string
      category: string
      state?: string
      totalHours?: string
      renewalPeriodYears?: string
      specialRequirements?: string
      ceCategories?: CECategory[]
    }) => apiPost('/admin/professions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professions'] })
      resetAddModal()
    },
  })

  const updateProfessionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; abbreviation?: string | null; category?: string | null; isActive?: boolean } }) =>
      apiPut(`/admin/professions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professions'] })
      setEditingProfessionId(null)
    },
  })

  const deleteProfessionMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/professions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professions'] })
      setDeletingProfessionId(null)
    },
    onError: (error: any) => {
      alert(error?.message || 'Failed to delete profession')
    },
  })

  const mergeProfessionMutation = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      apiPost(`/admin/professions/${sourceId}/merge/${targetId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professions'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
      setMergingProfession(null)
      setMergeTargetId(null)
      setMergeSearchQuery('')
    },
  })

  // AI Lookup mutation for finding CE requirements
  const lookupMutation = useMutation({
    mutationFn: ({ professionName, state }: { professionName: string; state: string }) =>
      apiPost<{
        totalHours: number
        renewalPeriodYears: number
        categories: Array<{ name: string; hours: number | null; isRequired: boolean; isOneTime: boolean }>
        specialRequirements: string
        confidence: number
      }>('/professions/ai-lookup', { professionName, state }),
  })

  // Save looked-up requirements
  const saveLookupMutation = useMutation({
    mutationFn: async (data: {
      professionId: string
      state: string
      totalHours: number
      renewalPeriodYears: number
      notes: string
      categories: Array<{ name: string; hours: string; isOneTime: boolean; isPediatric: boolean }>
    }) => {
      const reqResult = await apiPost<{ id: string }>('/admin/ce-requirements', {
        professionId: data.professionId,
        state: data.state,
        totalHours: data.totalHours,
        renewalPeriodYears: data.renewalPeriodYears,
        notes: data.notes || undefined,
      })
      
      for (const cat of data.categories) {
        await apiPost(`/admin/ce-requirements/${reqResult.id}/categories`, {
          name: cat.name,
          categoryType: cat.hours ? 'hours' : 'required',
          requiredHours: cat.hours || null,
          isOneTime: cat.isOneTime || false,
          isPediatricContent: cat.isPediatric || false,
        })
      }
      return reqResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'professions-missing-requirements'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'professions-missing-state-requirements'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
      queryClient.invalidateQueries({ queryKey: ['professions'] })
      setLookupResults(null)
      setLookupProfessionId(null)
      setLookupState('')
      setEditingLookupResults(false)
    },
  })

  const handleStartLookup = (profId: string) => {
    setLookupProfessionId(profId)
    setLookupState('')
    setLookupResults(null)
    setEditingLookupResults(false)
  }

  const handleDoLookup = async () => {
    if (!lookupProfessionId || !lookupState) return
    const allProfs = missingReqsProfessions || []
    let prof: Profession | undefined = allProfs.find(p => p.id === lookupProfessionId)
    if (!prof) {
      const missingStateProf = (missingStateReqs || []).find(m => m.profession.id === lookupProfessionId)
      prof = missingStateProf?.profession
    }
    if (!prof) {
      prof = professions.find(p => p.id === lookupProfessionId)
    }
    if (!prof) return
    
    try {
      const result = await lookupMutation.mutateAsync({
        professionName: prof.name,
        state: lookupState === 'NREMT' ? 'NREMT' : US_STATES[lookupState] || lookupState,
      })
      setLookupResults({
        professionId: lookupProfessionId,
        professionName: prof.name,
        state: lookupState,
        totalHours: result.totalHours,
        renewalPeriodYears: result.renewalPeriodYears,
        categories: result.categories || [],
        specialRequirements: result.specialRequirements || '',
        confidence: result.confidence || 0,
      })
      setLookupEditHours(result.totalHours?.toString() || '')
      setLookupEditRenewalYears(result.renewalPeriodYears?.toString() || '2')
      setLookupEditNotes(result.specialRequirements || '')
      setLookupEditCategories(
        (result.categories || []).map(c => ({
          name: c.name,
          hours: c.hours?.toString() || '',
          isOneTime: c.isOneTime || false,
          isPediatric: false,
        }))
      )
    } catch (err) {
      console.error('Lookup failed:', err)
    }
  }

  const handleSaveLookupResults = () => {
    if (!lookupProfessionId || !lookupState) return
    saveLookupMutation.mutate({
      professionId: lookupProfessionId,
      state: lookupState,
      totalHours: parseFloat(lookupEditHours) || 0,
      renewalPeriodYears: parseInt(lookupEditRenewalYears) || 2,
      notes: lookupEditNotes,
      categories: lookupEditCategories,
    })
  }

  const handleCancelLookup = () => {
    setLookupProfessionId(null)
    setLookupState('')
    setLookupResults(null)
    setEditingLookupResults(false)
    lookupMutation.reset()
  }

  const mergeTargetOptions = useMemo(() => {
    if (!mergingProfession) return []
    return professions
      .filter(p => p.id !== mergingProfession.id)
      .filter(p => 
        !mergeSearchQuery ||
        p.name.toLowerCase().includes(mergeSearchQuery.toLowerCase()) ||
        (p.abbreviation && p.abbreviation.toLowerCase().includes(mergeSearchQuery.toLowerCase()))
      )
  }, [professions, mergingProfession, mergeSearchQuery])

  const createRequirementMutation = useMutation({
    mutationFn: (data: { professionId: string; state: string; totalHours: number; renewalPeriodYears: number; notes?: string }) =>
      apiPost('/admin/ce-requirements', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
      setAddingRequirementToProfId(null)
      setNewReqState('')
      setNewReqTotalHours('')
      setNewReqRenewalYears('2')
      setNewReqNotes('')
    },
  })

  const updateRequirementMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { totalHours?: number | null; renewalPeriodYears?: number; notes?: string | null; followsNremt?: boolean } }) =>
      apiPut(`/admin/ce-requirements/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
      setEditingRequirementId(null)
    },
  })

  const deleteRequirementMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/ce-requirements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
    },
  })

  const createCategoryMutation = useMutation({
    mutationFn: ({ requirementId, data }: { requirementId: string; data: { name: string; requiredHours?: string | null; isOneTime?: boolean; isPediatricContent?: boolean } }) =>
      apiPost(`/admin/ce-requirements/${requirementId}/categories`, {
        name: data.name,
        categoryType: data.requiredHours ? 'hours' : 'required',
        requiredHours: data.requiredHours || null,
        isOneTime: data.isOneTime || false,
        isPediatricContent: data.isPediatricContent || false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
      setAddingCategoryToReqId(null)
      setNewCategoryName('')
      setNewCategoryHours('')
      setNewCategoryIsOneTime(false)
      setNewCategoryIsPediatric(false)
    },
  })

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; requiredHours?: string | null; isOneTime?: boolean; isPediatricContent?: boolean } }) =>
      apiPatch(`/admin/ce-categories/${id}`, {
        name: data.name,
        categoryType: data.requiredHours ? 'hours' : 'required',
        requiredHours: data.requiredHours || null,
        isOneTime: data.isOneTime || false,
        isPediatricContent: data.isPediatricContent || false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
      setEditingCategoryId(null)
    },
  })

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/admin/ce-categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profession-requirements'] })
    },
  })

  const startEditRequirement = (req: CeRequirementWithCategories) => {
    setEditingRequirementId(req.id)
    setEditTotalHours(req.totalHours?.toString() || '')
    setEditRenewalYears(req.renewalPeriodYears?.toString() || '2')
    setEditNotes(req.notes || '')
    setEditFollowsNremt(req.followsNremt || false)
  }

  const startEditCategory = (cat: CeRequirementWithCategories['categories'][0]) => {
    setEditingCategoryId(cat.id)
    setEditCatName(cat.name)
    setEditCatHours(cat.requiredHours || '')
    setEditCatIsOneTime(cat.isOneTime)
    setEditCatIsPediatric(cat.isPediatricContent)
  }

  const handleUpdateCategory = (id: string) => {
    updateCategoryMutation.mutate({
      id,
      data: {
        name: editCatName,
        requiredHours: editCatHours || null,
        isOneTime: editCatIsOneTime,
        isPediatricContent: editCatIsPediatric,
      }
    })
  }

  const handleAddCategoryToRequirement = (requirementId: string) => {
    if (!newCategoryName.trim()) return
    createCategoryMutation.mutate({
      requirementId,
      data: {
        name: newCategoryName.trim(),
        requiredHours: newCategoryHours || null,
        isOneTime: newCategoryIsOneTime,
        isPediatricContent: newCategoryIsPediatric,
      }
    })
  }

  const handleUpdateRequirement = (id: string) => {
    updateRequirementMutation.mutate({
      id,
      data: {
        totalHours: editTotalHours ? parseFloat(editTotalHours) : null,
        renewalPeriodYears: parseInt(editRenewalYears) || 2,
        notes: editNotes || null,
        followsNremt: editFollowsNremt,
      }
    })
  }

  const resetAddModal = () => {
    setShowAddModal(false)
    setNewProfessionName('')
    setNewAbbreviation('')
    setNewCategory('Healthcare')
    setNewState('')
    setNewTotalHours('')
    setNewRenewalYears('2')
    setNewSpecialReqs('')
    setNewCeCategories([])
    setNewCatName('')
    setNewCatHours('')
  }

  const startEditProfession = (prof: Profession) => {
    setEditingProfessionId(prof.id)
    setEditProfessionName(prof.name)
    setEditProfessionAbbreviation(prof.abbreviation || '')
    setEditProfessionCategory(prof.category || 'Other')
    setEditProfessionIsActive(prof.isActive)
  }

  const cancelEditProfession = () => {
    setEditingProfessionId(null)
    setEditProfessionName('')
    setEditProfessionAbbreviation('')
    setEditProfessionCategory('')
    setEditProfessionIsActive(true)
  }

  const handleUpdateProfession = () => {
    if (!editingProfessionId || !editProfessionName.trim()) return
    updateProfessionMutation.mutate({
      id: editingProfessionId,
      data: {
        name: editProfessionName.trim(),
        abbreviation: editProfessionAbbreviation.trim() || null,
        category: editProfessionCategory || null,
        isActive: editProfessionIsActive,
      },
    })
  }

  const handleAddCategory = () => {
    if (newCatName.trim()) {
      setNewCeCategories([...newCeCategories, {
        name: newCatName.trim(),
        requiredHours: newCatHours ? parseFloat(newCatHours) : undefined
      }])
      setNewCatName('')
      setNewCatHours('')
    }
  }

  const handleRemoveCategory = (index: number) => {
    setNewCeCategories(newCeCategories.filter((_, i) => i !== index))
  }

  const handleCreateProfession = () => {
    if (!newProfessionName.trim()) {
      alert('Please enter a profession name')
      return
    }
    createProfessionMutation.mutate({
      name: newProfessionName.trim(),
      abbreviation: newAbbreviation.trim() || undefined,
      category: newCategory,
      state: newState || undefined,
      totalHours: newTotalHours || undefined,
      renewalPeriodYears: newRenewalYears || undefined,
      specialRequirements: newSpecialReqs || undefined,
      ceCategories: newCeCategories.length > 0 ? newCeCategories : undefined
    })
  }

  const requests = requestsData?.requests || []
  const pendingRequests = requests.filter(r => r.status === 'pending')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CE Management</h1>
          <p className="text-gray-500 mt-1">Manage professions and CE requirements</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Profession
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('requests')}
          className={`pb-3 px-1 font-medium text-sm transition-colors relative ${
            activeTab === 'requests'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Pending Requests
          {pendingRequests.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {pendingRequests.length}
            </span>
          )}
          {activeTab === 'requests' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('professions')}
          className={`pb-3 px-1 font-medium text-sm transition-colors relative ${
            activeTab === 'professions'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          All Professions ({professions.length})
          {activeTab === 'professions' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('missing')}
          className={`pb-3 px-1 font-medium text-sm transition-colors relative ${
            activeTab === 'missing'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Missing Requirements
          {(missingReqsProfessions?.length || 0) > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
              {missingReqsProfessions?.length}
            </span>
          )}
          {activeTab === 'missing' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
          )}
        </button>
      </div>

      {/* Pending Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          {loadingRequests ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : pendingRequests.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No pending profession requests</p>
            </div>
          ) : (
            pendingRequests.map((request) => (
              <div key={request.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {request.professionName} - {request.state}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {request.totalHours} hours • {request.renewalPeriodYears} year cycle
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {request.aiConfidence && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.aiConfidence >= 0.8 ? 'bg-green-50 text-green-600' :
                          request.aiConfidence >= 0.6 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'
                        }`}>
                          {Math.round(request.aiConfidence * 100)}% confidence
                        </span>
                      )}
                      {expandedRequest === request.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedRequest === request.id && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    {/* Categories */}
                    {request.categories && request.categories.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">CE Categories</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {request.categories.map((cat, idx) => (
                            <div key={idx} className="bg-white p-2 rounded border border-gray-200 text-sm">
                              <span className="font-medium">{cat.name}</span>
                              {cat.hours && <span className="text-gray-500 ml-1">({cat.hours}h)</span>}
                              {cat.isRequired && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 ml-1">Required</span>}
                              {cat.isOneTime && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ml-1">One-time</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Special Requirements */}
                    {request.specialRequirements && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Special Requirements</h4>
                        <p className="text-sm text-gray-600 bg-white p-2 rounded border border-gray-200">
                          {request.specialRequirements}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => approveMutation.mutate(request.id)}
                        disabled={approveMutation.isPending}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </button>
                      <button
                        onClick={() => rejectMutation.mutate(request.id)}
                        disabled={rejectMutation.isPending}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Professions Tab */}
      {activeTab === 'professions' && (
        <div className="space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={professionSearchQuery}
              onChange={(e) => setProfessionSearchQuery(e.target.value)}
              placeholder="Search professions..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {professionSearchQuery && (
              <button
                onClick={() => setProfessionSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          {loadingProfessions ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : professions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No professions found</p>
              <p className="text-gray-400 text-sm mt-1">Professions will appear here once they are approved</p>
            </div>
          ) : (
            professions
              .filter(prof => 
                !professionSearchQuery || 
                prof.name.toLowerCase().includes(professionSearchQuery.toLowerCase()) ||
                (prof.abbreviation && prof.abbreviation.toLowerCase().includes(professionSearchQuery.toLowerCase())) ||
                (prof.category && prof.category.toLowerCase().includes(professionSearchQuery.toLowerCase()))
              )
              .map((prof) => (
              <div key={prof.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {editingProfessionId === prof.id ? (
                  <div className="p-4 bg-blue-50 border-b border-blue-100">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Profession Name *</label>
                          <input
                            type="text"
                            value={editProfessionName}
                            onChange={(e) => setEditProfessionName(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Abbreviation</label>
                          <input
                            type="text"
                            value={editProfessionAbbreviation}
                            onChange={(e) => setEditProfessionAbbreviation(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                          <select
                            value={editProfessionCategory}
                            onChange={(e) => setEditProfessionCategory(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {PROFESSION_CATEGORIES.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={editProfessionIsActive}
                              onChange={(e) => setEditProfessionIsActive(e.target.checked)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            Active
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <button
                          onClick={handleUpdateProfession}
                          disabled={updateProfessionMutation.isPending || !editProfessionName.trim()}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {updateProfessionMutation.isPending ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                          Save
                        </button>
                        <button
                          onClick={cancelEditProfession}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="flex-1 flex items-center gap-3 text-left"
                        onClick={() => {
                          console.log('Clicked profession:', prof.id, prof.name)
                          setExpandedProfession(expandedProfession === prof.id ? null : prof.id)
                        }}
                      >
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <BookOpen className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{prof.name}</h3>
                          <p className="text-sm text-gray-500">
                            {prof.abbreviation && `${prof.abbreviation} • `}{prof.category || 'Uncategorized'}
                          </p>
                        </div>
                      </button>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditProfession(prof) }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit profession"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setMergingProfession(prof); setMergeTargetId(null); setMergeSearchQuery('') }}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="Merge into another profession"
                        >
                          <GitMerge className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeletingProfessionId(prof.id) }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete profession"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {prof.isActive ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600">Active</span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Inactive</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedProfession(expandedProfession === prof.id ? null : prof.id)}
                          className="p-1"
                        >
                          {expandedProfession === prof.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {expandedProfession === prof.id && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">CE Requirements by State</h4>
                    {loadingRequirements ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                    ) : !professionRequirements || professionRequirements.length === 0 ? (
                      <div className="py-2">
                        <p className="text-gray-500 text-sm mb-3">No CE requirements defined for this profession yet.</p>
                        {addingRequirementToProfId === prof.id ? (
                          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">State *</label>
                                <select
                                  value={newReqState}
                                  onChange={(e) => setNewReqState(e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select state...</option>
                                  {Object.entries(US_STATES).map(([code, name]) => (
                                    <option key={code} value={code}>{name}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Total Hours *</label>
                                <input
                                  type="number"
                                  value={newReqTotalHours}
                                  onChange={(e) => setNewReqTotalHours(e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., 40"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Period (years)</label>
                                <input
                                  type="number"
                                  value={newReqRenewalYears}
                                  onChange={(e) => setNewReqRenewalYears(e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., 2"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                                <input
                                  type="text"
                                  value={newReqNotes}
                                  onChange={(e) => setNewReqNotes(e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Special requirements..."
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  if (!newReqState || !newReqTotalHours) return
                                  createRequirementMutation.mutate({
                                    professionId: prof.id,
                                    state: newReqState,
                                    totalHours: parseInt(newReqTotalHours),
                                    renewalPeriodYears: parseInt(newReqRenewalYears) || 2,
                                    notes: newReqNotes || undefined,
                                  })
                                }}
                                disabled={!newReqState || !newReqTotalHours || createRequirementMutation.isPending}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                <Check className="w-4 h-4" />
                                Add
                              </button>
                              <button
                                onClick={() => {
                                  setAddingRequirementToProfId(null)
                                  setNewReqState('')
                                  setNewReqTotalHours('')
                                  setNewReqRenewalYears('2')
                                  setNewReqNotes('')
                                }}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAddingRequirementToProfId(prof.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4" />
                            Add CE Requirements
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {professionRequirements.map((req) => (
                          <div key={req.id} className="bg-white p-3 rounded-lg border border-gray-200">
                            {editingRequirementId === req.id ? (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-gray-900">{US_STATES[req.state] || req.state}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Total Hours</label>
                                    <input
                                      type="number"
                                      value={editTotalHours}
                                      onChange={(e) => setEditTotalHours(e.target.value)}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="e.g., 40"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Period (years)</label>
                                    <input
                                      type="number"
                                      value={editRenewalYears}
                                      onChange={(e) => setEditRenewalYears(e.target.value)}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder="e.g., 2"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                                  <textarea
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Any special requirements or notes..."
                                    rows={2}
                                  />
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={editFollowsNremt}
                                    onChange={(e) => setEditFollowsNremt(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">Follows NREMT Requirements</span>
                                </label>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleUpdateRequirement(req.id)}
                                    disabled={updateRequirementMutation.isPending}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                  >
                                    <Check className="w-4 h-4" />
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingRequirementId(null)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
                                  >
                                    <X className="w-4 h-4" />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{US_STATES[req.state] || req.state}</span>
                                    {req.followsNremt && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                                        NREMT
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">
                                      {req.totalHours}h / {req.renewalPeriodYears} year{req.renewalPeriodYears !== 1 ? 's' : ''}
                                    </span>
                                    <button
                                      onClick={() => startEditRequirement(req)}
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Delete CE requirements for ${US_STATES[req.state] || req.state}?`)) {
                                          deleteRequirementMutation.mutate(req.id)
                                        }
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {req.notes && (
                                  <p className="text-sm text-gray-600 mb-2">{req.notes}</p>
                                )}
                                
                                {/* Categories Section */}
                                <div className={`mt-3 pt-3 border-t border-gray-100 ${req.followsNremt ? 'opacity-60' : ''}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-gray-500 uppercase">Categories</span>
                                      {req.followsNremt && (
                                        <span className="text-xs text-gray-400 italic">(managed by NREMT)</span>
                                      )}
                                    </div>
                                    {!req.followsNremt && (
                                      <button
                                        onClick={() => setAddingCategoryToReqId(addingCategoryToReqId === req.id ? null : req.id)}
                                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                      >
                                        <Plus className="w-3 h-3" />
                                        Add Category
                                      </button>
                                    )}
                                  </div>
                                  
                                  {/* Add Category Form */}
                                  {!req.followsNremt && addingCategoryToReqId === req.id && (
                                    <div className="bg-blue-50 p-3 rounded-lg mb-3 space-y-2">
                                      <div className="grid grid-cols-2 gap-2">
                                        <input
                                          type="text"
                                          value={newCategoryName}
                                          onChange={(e) => setNewCategoryName(e.target.value)}
                                          placeholder="Category name"
                                          className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <input
                                          type="number"
                                          value={newCategoryHours}
                                          onChange={(e) => setNewCategoryHours(e.target.value)}
                                          placeholder="Required hours (optional)"
                                          className="px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={newCategoryIsOneTime}
                                            onChange={(e) => setNewCategoryIsOneTime(e.target.checked)}
                                            className="w-3.5 h-3.5 text-blue-600 rounded"
                                          />
                                          One-time requirement
                                        </label>
                                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={newCategoryIsPediatric}
                                            onChange={(e) => setNewCategoryIsPediatric(e.target.checked)}
                                            className="w-3.5 h-3.5 text-blue-600 rounded"
                                          />
                                          Pediatric content
                                        </label>
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => handleAddCategoryToRequirement(req.id)}
                                          disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                        >
                                          Add
                                        </button>
                                        <button
                                          onClick={() => setAddingCategoryToReqId(null)}
                                          className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Category List */}
                                  {req.categories && req.categories.length > 0 ? (
                                    <div className="space-y-2">
                                      {req.categories.map((cat) => (
                                        <div key={cat.id}>
                                          {!req.followsNremt && editingCategoryId === cat.id ? (
                                            <div className="bg-yellow-50 p-2 rounded space-y-2">
                                              <div className="grid grid-cols-2 gap-2">
                                                <input
                                                  type="text"
                                                  value={editCatName}
                                                  onChange={(e) => setEditCatName(e.target.value)}
                                                  placeholder="Category name"
                                                  className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                                <input
                                                  type="number"
                                                  value={editCatHours}
                                                  onChange={(e) => setEditCatHours(e.target.value)}
                                                  placeholder="Required hours"
                                                  className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                              </div>
                                              <div className="flex items-center gap-4">
                                                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={editCatIsOneTime}
                                                    onChange={(e) => setEditCatIsOneTime(e.target.checked)}
                                                    className="w-3.5 h-3.5 text-blue-600 rounded"
                                                  />
                                                  One-time
                                                </label>
                                                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={editCatIsPediatric}
                                                    onChange={(e) => setEditCatIsPediatric(e.target.checked)}
                                                    className="w-3.5 h-3.5 text-blue-600 rounded"
                                                  />
                                                  Pediatric
                                                </label>
                                              </div>
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={() => handleUpdateCategory(cat.id)}
                                                  disabled={updateCategoryMutation.isPending}
                                                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                                >
                                                  Save
                                                </button>
                                                <button
                                                  onClick={() => setEditingCategoryId(null)}
                                                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="flex items-center justify-between bg-gray-50 px-2 py-1.5 rounded">
                                              <span className="text-sm text-gray-700">
                                                {cat.name}
                                                {cat.requiredHours && <span className="text-gray-500"> ({cat.requiredHours}h)</span>}
                                                {cat.isOneTime && <span className="text-xs text-orange-600 ml-1">One-time</span>}
                                                {cat.isPediatricContent && <span className="text-xs text-purple-600 ml-1">Pediatric</span>}
                                              </span>
                                              {!req.followsNremt && (
                                                <div className="flex items-center gap-1">
                                                  <button
                                                    onClick={() => startEditCategory(cat)}
                                                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                                                    title="Edit category"
                                                  >
                                                    <Edit2 className="w-3 h-3" />
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      if (window.confirm(`Delete category "${cat.name}"?`)) {
                                                        deleteCategoryMutation.mutate(cat.id)
                                                      }
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                                                    title="Delete category"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400 italic">No categories defined</p>
                                  )}
                                </div>

                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add State with AI Lookup */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      {lookupProfessionId === prof.id ? (
                        <div className="space-y-4">
                          {!lookupResults && (
                            <div className="flex items-end gap-3">
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search CE requirements for a new state</label>
                                <select
                                  value={lookupState}
                                  onChange={(e) => setLookupState(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                  <option value="">Select a state...</option>
                                  <option value="NREMT">NREMT (National)</option>
                                  {Object.entries(US_STATES)
                                    .filter(([code]) => {
                                      const existingStates = (professionRequirements || []).map(r => r.state)
                                      return !existingStates.includes(code)
                                    })
                                    .map(([code, name]) => (
                                    <option key={code} value={code}>{name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={handleDoLookup}
                                  disabled={!lookupState || lookupMutation.isPending}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                  {lookupMutation.isPending ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Searching...
                                    </>
                                  ) : (
                                    <>
                                      <Search className="w-4 h-4" />
                                      Search
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={handleCancelLookup}
                                  className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {lookupMutation.isError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                              Search failed. Please try again or add requirements manually.
                            </div>
                          )}

                          {lookupResults && lookupResults.professionId === prof.id && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-gray-900 text-sm">
                                  Results for {prof.name} - {lookupState === 'NREMT' ? 'NREMT' : US_STATES[lookupState] || lookupState}
                                </h4>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  lookupResults.confidence >= 0.8 ? 'bg-green-50 text-green-600' :
                                  lookupResults.confidence >= 0.6 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'
                                }`}>
                                  {Math.round(lookupResults.confidence * 100)}% accuracy
                                </span>
                              </div>

                              <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Total CE Hours</label>
                                    <input
                                      type="number"
                                      value={lookupEditHours}
                                      onChange={(e) => setLookupEditHours(e.target.value)}
                                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Period (Years)</label>
                                    <input
                                      type="number"
                                      value={lookupEditRenewalYears}
                                      onChange={(e) => setLookupEditRenewalYears(e.target.value)}
                                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes / Special Requirements</label>
                                  <textarea
                                    value={lookupEditNotes}
                                    onChange={(e) => setLookupEditNotes(e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Any special requirements or notes..."
                                  />
                                </div>

                                <div>
                                  <label className="text-xs font-medium text-gray-600 mb-2 block">CE Categories</label>
                                  {lookupEditCategories.length > 0 && (
                                    <div className="space-y-2 mb-3">
                                      {lookupEditCategories.map((cat, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                                          <input
                                            type="text"
                                            value={cat.name}
                                            onChange={(e) => {
                                              const updated = [...lookupEditCategories]
                                              updated[idx] = { ...updated[idx], name: e.target.value }
                                              setLookupEditCategories(updated)
                                            }}
                                            className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                          <input
                                            type="number"
                                            value={cat.hours}
                                            onChange={(e) => {
                                              const updated = [...lookupEditCategories]
                                              updated[idx] = { ...updated[idx], hours: e.target.value }
                                              setLookupEditCategories(updated)
                                            }}
                                            placeholder="Hours"
                                            className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          />
                                          <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                                            <input
                                              type="checkbox"
                                              checked={cat.isOneTime}
                                              onChange={(e) => {
                                                const updated = [...lookupEditCategories]
                                                updated[idx] = { ...updated[idx], isOneTime: e.target.checked }
                                                setLookupEditCategories(updated)
                                              }}
                                              className="w-3 h-3"
                                            />
                                            One-time
                                          </label>
                                          <button
                                            onClick={() => setLookupEditCategories(lookupEditCategories.filter((_, i) => i !== idx))}
                                            className="p-1 text-gray-400 hover:text-red-600"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={lookupNewCatName}
                                      onChange={(e) => setLookupNewCatName(e.target.value)}
                                      placeholder="Category name"
                                      className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                    <input
                                      type="number"
                                      value={lookupNewCatHours}
                                      onChange={(e) => setLookupNewCatHours(e.target.value)}
                                      placeholder="Hours"
                                      className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                    <button
                                      onClick={() => {
                                        if (lookupNewCatName.trim()) {
                                          setLookupEditCategories([...lookupEditCategories, {
                                            name: lookupNewCatName.trim(),
                                            hours: lookupNewCatHours,
                                            isOneTime: false,
                                            isPediatric: false,
                                          }])
                                          setLookupNewCatName('')
                                          setLookupNewCatHours('')
                                        }
                                      }}
                                      disabled={!lookupNewCatName.trim()}
                                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                                    >
                                      Add
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div className="flex justify-end gap-3">
                                <button
                                  onClick={handleCancelLookup}
                                  className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => {
                                    setLookupResults(null)
                                    setLookupState('')
                                  }}
                                  className="px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 text-sm"
                                >
                                  Search Different State
                                </button>
                                <button
                                  onClick={handleSaveLookupResults}
                                  disabled={saveLookupMutation.isPending || !lookupEditHours}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                  {saveLookupMutation.isPending ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4" />
                                      Save Requirements
                                    </>
                                  )}
                                </button>
                              </div>

                              {saveLookupMutation.isError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                  Failed to save requirements. Please try again.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStartLookup(prof.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add State
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Missing Requirements Tab */}
      {activeTab === 'missing' && (
        <div className="space-y-4">
          {loadingMissing ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : !missingReqsProfessions || missingReqsProfessions.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-gray-500">All professions have CE requirements configured</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                {missingReqsProfessions.length} profession{missingReqsProfessions.length !== 1 ? 's' : ''} without CE requirements.
                Use "Find Requirements" to search for and add requirements.
              </p>
              {missingReqsProfessions.map((prof) => (
                <div key={prof.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-50 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {prof.name}
                            {prof.abbreviation && (
                              <span className="text-gray-500 text-sm ml-2">({prof.abbreviation})</span>
                            )}
                          </h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-gray-500">{prof.category || 'Uncategorized'}</p>
                            {prof.requestedStates && prof.requestedStates.length > 0 && (
                              <>
                                <span className="text-gray-300">|</span>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-xs text-gray-500">Requested:</span>
                                  {prof.requestedStates.map((rs) => (
                                    <span
                                      key={rs.state}
                                      className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded font-medium cursor-pointer hover:bg-blue-100 transition-colors"
                                      onClick={() => {
                                        handleStartLookup(prof.id)
                                        const stateCode = Object.entries(US_STATES).find(([_, name]) => name === rs.state)?.[0] || rs.state
                                        setLookupState(stateCode)
                                      }}
                                      title={`${rs.userCount} user${rs.userCount !== 1 ? 's' : ''} requested this state - click to search`}
                                    >
                                      {rs.state}
                                      {rs.userCount > 1 && <span className="ml-0.5 text-blue-500">({rs.userCount})</span>}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleStartLookup(prof.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Find Requirements
                      </button>
                    </div>
                  </div>

                  {/* Lookup Panel for this profession */}
                  {lookupProfessionId === prof.id && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4">
                      {/* State Selector + Search Button */}
                      {!lookupResults && (
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <select
                              value={lookupState}
                              onChange={(e) => setLookupState(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="">Select a state...</option>
                              <option value="NREMT">NREMT (National)</option>
                              {Object.entries(US_STATES).map(([code, name]) => (
                                <option key={code} value={code}>{name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleDoLookup}
                              disabled={!lookupState || lookupMutation.isPending}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {lookupMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Searching...
                                </>
                              ) : (
                                <>
                                  <Search className="w-4 h-4" />
                                  Search
                                </>
                              )}
                            </button>
                            <button
                              onClick={handleCancelLookup}
                              className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {lookupMutation.isError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                          Search failed. Please try again or add requirements manually.
                        </div>
                      )}

                      {/* Results Display */}
                      {lookupResults && lookupResults.professionId === prof.id && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900 text-sm">
                              Results for {prof.name} - {lookupState === 'NREMT' ? 'NREMT' : US_STATES[lookupState] || lookupState}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              lookupResults.confidence >= 0.8 ? 'bg-green-50 text-green-600' :
                              lookupResults.confidence >= 0.6 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'
                            }`}>
                              {Math.round(lookupResults.confidence * 100)}% accuracy
                            </span>
                          </div>

                          {/* Editable Fields */}
                          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Total CE Hours</label>
                                <input
                                  type="number"
                                  value={lookupEditHours}
                                  onChange={(e) => setLookupEditHours(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Period (Years)</label>
                                <input
                                  type="number"
                                  value={lookupEditRenewalYears}
                                  onChange={(e) => setLookupEditRenewalYears(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Notes / Special Requirements</label>
                              <textarea
                                value={lookupEditNotes}
                                onChange={(e) => setLookupEditNotes(e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Any special requirements or notes..."
                              />
                            </div>

                            {/* Categories */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-medium text-gray-600">CE Categories</label>
                              </div>
                              
                              {lookupEditCategories.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {lookupEditCategories.map((cat, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                                      <input
                                        type="text"
                                        value={cat.name}
                                        onChange={(e) => {
                                          const updated = [...lookupEditCategories]
                                          updated[idx] = { ...updated[idx], name: e.target.value }
                                          setLookupEditCategories(updated)
                                        }}
                                        className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      />
                                      <input
                                        type="number"
                                        value={cat.hours}
                                        onChange={(e) => {
                                          const updated = [...lookupEditCategories]
                                          updated[idx] = { ...updated[idx], hours: e.target.value }
                                          setLookupEditCategories(updated)
                                        }}
                                        placeholder="Hours"
                                        className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      />
                                      <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                                        <input
                                          type="checkbox"
                                          checked={cat.isOneTime}
                                          onChange={(e) => {
                                            const updated = [...lookupEditCategories]
                                            updated[idx] = { ...updated[idx], isOneTime: e.target.checked }
                                            setLookupEditCategories(updated)
                                          }}
                                          className="w-3 h-3"
                                        />
                                        One-time
                                      </label>
                                      <button
                                        onClick={() => setLookupEditCategories(lookupEditCategories.filter((_, i) => i !== idx))}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Add Category */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={lookupNewCatName}
                                  onChange={(e) => setLookupNewCatName(e.target.value)}
                                  placeholder="Category name"
                                  className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <input
                                  type="number"
                                  value={lookupNewCatHours}
                                  onChange={(e) => setLookupNewCatHours(e.target.value)}
                                  placeholder="Hours"
                                  className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <button
                                  onClick={() => {
                                    if (lookupNewCatName.trim()) {
                                      setLookupEditCategories([...lookupEditCategories, {
                                        name: lookupNewCatName.trim(),
                                        hours: lookupNewCatHours,
                                        isOneTime: false,
                                        isPediatric: false,
                                      }])
                                      setLookupNewCatName('')
                                      setLookupNewCatHours('')
                                    }
                                  }}
                                  disabled={!lookupNewCatName.trim()}
                                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex justify-end gap-3">
                            <button
                              onClick={handleCancelLookup}
                              className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                setLookupResults(null)
                                setLookupState('')
                              }}
                              className="px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 text-sm"
                            >
                              Search Different State
                            </button>
                            <button
                              onClick={handleSaveLookupResults}
                              disabled={saveLookupMutation.isPending || !lookupEditHours}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {saveLookupMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4" />
                                  Save Requirements
                                </>
                              )}
                            </button>
                          </div>

                          {saveLookupMutation.isError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                              Failed to save requirements. Please try again.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Missing State Requirements - professions that have some requirements but are missing states users requested */}
          {!loadingMissingStates && missingStateReqs && missingStateReqs.length > 0 && (
            <div className="space-y-3 mt-6">
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">User-Requested States Needing Requirements</h3>
                <p className="text-xs text-gray-500 mb-3">
                  These professions have CE requirements for some states, but users have requested states that aren't covered yet.
                </p>
              </div>
              {missingStateReqs.map((item) => (
                <div key={item.profession.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <Clock className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {item.profession.name}
                            {item.profession.abbreviation && (
                              <span className="text-gray-500 text-sm ml-2">({item.profession.abbreviation})</span>
                            )}
                          </h3>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <span className="text-xs text-gray-500">Missing:</span>
                            {item.missingStates.map((ms) => (
                              <span
                                key={ms.state}
                                className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded font-medium cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() => {
                                  handleStartLookup(item.profession.id)
                                  const stateCode = Object.entries(US_STATES).find(([_, name]) => name === ms.state)?.[0] || ms.state
                                  setLookupState(stateCode)
                                }}
                                title={`${ms.userCount} user${ms.userCount !== 1 ? 's' : ''} requested this state - click to search`}
                              >
                                {ms.state}
                                {ms.userCount > 1 && <span className="ml-0.5 text-blue-500">({ms.userCount})</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleStartLookup(item.profession.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Find Requirements
                      </button>
                    </div>
                  </div>

                  {/* Lookup Panel */}
                  {lookupProfessionId === item.profession.id && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4">
                      {!lookupResults && (
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <select
                              value={lookupState}
                              onChange={(e) => setLookupState(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="">Select a state...</option>
                              <option value="NREMT">NREMT (National)</option>
                              {Object.entries(US_STATES).map(([code, name]) => (
                                <option key={code} value={code}>{name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleDoLookup}
                              disabled={!lookupState || lookupMutation.isPending}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {lookupMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Searching...
                                </>
                              ) : (
                                <>
                                  <Search className="w-4 h-4" />
                                  Search
                                </>
                              )}
                            </button>
                            <button
                              onClick={handleCancelLookup}
                              className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {lookupMutation.isError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                          Search failed. Please try again or add requirements manually.
                        </div>
                      )}

                      {lookupResults && lookupResults.professionId === item.profession.id && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900 text-sm">
                              Results for {item.profession.name} - {lookupState === 'NREMT' ? 'NREMT' : US_STATES[lookupState] || lookupState}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              lookupResults.confidence >= 0.8 ? 'bg-green-50 text-green-600' :
                              lookupResults.confidence >= 0.6 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'
                            }`}>
                              {Math.round(lookupResults.confidence * 100)}% accuracy
                            </span>
                          </div>

                          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Total CE Hours</label>
                                <input
                                  type="number"
                                  value={lookupEditHours}
                                  onChange={(e) => setLookupEditHours(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Period (Years)</label>
                                <input
                                  type="number"
                                  value={lookupEditRenewalYears}
                                  onChange={(e) => setLookupEditRenewalYears(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Notes / Special Requirements</label>
                              <textarea
                                value={lookupEditNotes}
                                onChange={(e) => setLookupEditNotes(e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Any special requirements or notes..."
                              />
                            </div>

                            <div>
                              <label className="text-xs font-medium text-gray-600 mb-2 block">CE Categories</label>
                              {lookupEditCategories.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {lookupEditCategories.map((cat, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                                      <input
                                        type="text"
                                        value={cat.name}
                                        onChange={(e) => {
                                          const updated = [...lookupEditCategories]
                                          updated[idx] = { ...updated[idx], name: e.target.value }
                                          setLookupEditCategories(updated)
                                        }}
                                        className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      />
                                      <input
                                        type="number"
                                        value={cat.hours}
                                        onChange={(e) => {
                                          const updated = [...lookupEditCategories]
                                          updated[idx] = { ...updated[idx], hours: e.target.value }
                                          setLookupEditCategories(updated)
                                        }}
                                        placeholder="Hours"
                                        className="w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      />
                                      <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                                        <input
                                          type="checkbox"
                                          checked={cat.isOneTime}
                                          onChange={(e) => {
                                            const updated = [...lookupEditCategories]
                                            updated[idx] = { ...updated[idx], isOneTime: e.target.checked }
                                            setLookupEditCategories(updated)
                                          }}
                                          className="w-3 h-3"
                                        />
                                        One-time
                                      </label>
                                      <button
                                        onClick={() => setLookupEditCategories(lookupEditCategories.filter((_, i) => i !== idx))}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={lookupNewCatName}
                                  onChange={(e) => setLookupNewCatName(e.target.value)}
                                  placeholder="Category name"
                                  className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <input
                                  type="number"
                                  value={lookupNewCatHours}
                                  onChange={(e) => setLookupNewCatHours(e.target.value)}
                                  placeholder="Hours"
                                  className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <button
                                  onClick={() => {
                                    if (lookupNewCatName.trim()) {
                                      setLookupEditCategories([...lookupEditCategories, {
                                        name: lookupNewCatName.trim(),
                                        hours: lookupNewCatHours,
                                        isOneTime: false,
                                        isPediatric: false,
                                      }])
                                      setLookupNewCatName('')
                                      setLookupNewCatHours('')
                                    }
                                  }}
                                  disabled={!lookupNewCatName.trim()}
                                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end gap-3">
                            <button
                              onClick={handleCancelLookup}
                              className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                setLookupResults(null)
                                setLookupState('')
                              }}
                              className="px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 text-sm"
                            >
                              Search Different State
                            </button>
                            <button
                              onClick={handleSaveLookupResults}
                              disabled={saveLookupMutation.isPending || !lookupEditHours}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {saveLookupMutation.isPending ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4" />
                                  Save Requirements
                                </>
                              )}
                            </button>
                          </div>

                          {saveLookupMutation.isError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                              Failed to save requirements. Please try again.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Profession Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Profession</h2>
              <button onClick={resetAddModal} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Profession Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profession Name *</label>
                <input
                  type="text"
                  value={newProfessionName}
                  onChange={(e) => setNewProfessionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Registered Nurse"
                />
              </div>

              {/* Abbreviation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation</label>
                <input
                  type="text"
                  value={newAbbreviation}
                  onChange={(e) => setNewAbbreviation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., RN"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PROFESSION_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <hr className="my-4" />
              <p className="text-sm text-gray-500">Optionally add CE requirements for a specific state:</p>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a state (optional)</option>
                  {Object.entries(US_STATES).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Total Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total CE Hours</label>
                  <input
                    type="number"
                    value={newTotalHours}
                    onChange={(e) => setNewTotalHours(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Renewal Period (Years)</label>
                  <input
                    type="number"
                    value={newRenewalYears}
                    onChange={(e) => setNewRenewalYears(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 2"
                  />
                </div>
              </div>

              {/* Special Requirements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Requirements</label>
                <textarea
                  value={newSpecialReqs}
                  onChange={(e) => setNewSpecialReqs(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any special requirements..."
                />
              </div>

              {/* CE Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CE Categories</label>
                {newCeCategories.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {newCeCategories.map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg text-sm">
                        <span>{cat.name} {cat.requiredHours && `(${cat.requiredHours}h)`}</span>
                        <button onClick={() => handleRemoveCategory(idx)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Category name"
                  />
                  <input
                    type="number"
                    value={newCatHours}
                    onChange={(e) => setNewCatHours(e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Hours"
                  />
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={resetAddModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProfession}
                disabled={createProfessionMutation.isPending || !newProfessionName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createProfessionMutation.isPending ? 'Creating...' : 'Create Profession'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deletingProfessionId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Profession</h3>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete <span className="font-medium">{professions.find(p => p.id === deletingProfessionId)?.name}</span>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This will also remove all CE requirements and categories associated with this profession. This action cannot be undone.
            </p>
            {deleteProfessionMutation.isError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {(deleteProfessionMutation.error as any)?.message || 'Failed to delete profession. It may have active users.'}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setDeletingProfessionId(null); deleteProfessionMutation.reset() }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteProfessionMutation.mutate(deletingProfessionId)}
                disabled={deleteProfessionMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteProfessionMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {mergingProfession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <GitMerge className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Merge Profession</h3>
                </div>
                <button
                  onClick={() => { setMergingProfession(null); setMergeTargetId(null); setMergeSearchQuery('') }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                Merge <span className="font-medium text-gray-900">{mergingProfession.name}{mergingProfession.abbreviation ? ` (${mergingProfession.abbreviation})` : ''}</span> into another profession. All users and CE requirements will be moved to the target.
              </p>
            </div>

            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={mergeSearchQuery}
                  onChange={(e) => setMergeSearchQuery(e.target.value)}
                  placeholder="Search target profession..."
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {mergeTargetOptions.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No matching professions found</p>
              ) : (
                mergeTargetOptions.map(prof => (
                  <button
                    key={prof.id}
                    onClick={() => setMergeTargetId(prof.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between ${
                      mergeTargetId === prof.id
                        ? 'bg-purple-50 border border-purple-200 ring-1 ring-purple-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div>
                      <span className="font-medium text-gray-900 text-sm">{prof.name}</span>
                      <span className="text-gray-500 text-xs ml-2">
                        {prof.abbreviation && `${prof.abbreviation} • `}{prof.category || 'Uncategorized'}
                      </span>
                    </div>
                    {mergeTargetId === prof.id && (
                      <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>

            {mergeTargetId && (
              <div className="p-4 bg-amber-50 border-t border-amber-100">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    This will move all users and CE data from <span className="font-medium">{mergingProfession.name}</span> to <span className="font-medium">{professions.find(p => p.id === mergeTargetId)?.name}</span>, then delete <span className="font-medium">{mergingProfession.name}</span>. This cannot be undone.
                  </p>
                </div>
              </div>
            )}

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setMergingProfession(null); setMergeTargetId(null); setMergeSearchQuery('') }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (mergeTargetId) {
                    mergeProfessionMutation.mutate({ sourceId: mergingProfession.id, targetId: mergeTargetId })
                  }
                }}
                disabled={!mergeTargetId || mergeProfessionMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center gap-2"
              >
                {mergeProfessionMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    Merging...
                  </>
                ) : (
                  <>
                    <GitMerge className="w-4 h-4" />
                    Merge
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
